import React, { useState, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import { database, subscribeToPath } from '../../services/database';
import { TaskCategory, TaskTemplate, TaskSubTaskTemplate, TaskSubStepTemplate } from '../../types';
import { toast } from 'react-hot-toast';
import { Reorder } from 'motion/react';

import TaskImportView from './TaskImportView';

interface TaskConfigurationViewProps {
    isAdmin?: boolean;
}

const TaskConfigurationView: React.FC<TaskConfigurationViewProps> = ({ isAdmin = false }) => {
    const [activeSubTab, setActiveSubTab] = useState<'config' | 'import'>('config');
    const [categories, setCategories] = useState<{ [id: string]: TaskCategory }>({});
    const [templates, setTemplates] = useState<{ [id: string]: TaskTemplate }>({});
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [isEditingCategory, setIsEditingCategory] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Partial<TaskCategory> | null>(null);

    const [isEditingTemplate, setIsEditingTemplate] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<Partial<TaskTemplate> | null>(null);
    const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(null);

    // Subscribe to data
    useEffect(() => {
        const unsubCat = subscribeToPath('taskCategories', (data) => setCategories(data || {}));
        const unsubTpl = subscribeToPath('taskTemplates', (data) => setTemplates(data || {}));
        return () => {
            unsubCat();
            unsubTpl();
        };
    }, []);

    const categoriesList = Object.values(categories).sort((a, b) => a.name.localeCompare(b.name));
    const filteredTemplates = Object.values(templates)
        .filter(t => t.categoryId === selectedCategoryId)
        .sort((a, b) => a.title.localeCompare(b.title));

    const handleSaveCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingCategory?.name) return;

        try {
            if (editingCategory.id) {
                await database.updateTaskCategory(editingCategory.id, editingCategory);
                toast.success('Category updated');
            } else {
                await database.createTaskCategory(editingCategory);
                toast.success('Category created');
            }
            setIsEditingCategory(false);
            setEditingCategory(null);
        } catch (error) {
            toast.error('Failed to save category');
        }
    };

    const handleSaveTemplate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingTemplate?.title || !selectedCategoryId) return;

        try {
            const data = {
                ...editingTemplate,
                categoryId: selectedCategoryId,
                active: editingTemplate.active ?? true,
                subTasks: editingTemplate.subTasks || []
            };

            if (editingTemplate.id) {
                await database.updateTaskTemplate(editingTemplate.id, data);
                toast.success('Task updated');
            } else {
                await database.createTaskTemplate(data);
                toast.success('Task created');
            }
            setIsEditingTemplate(false);
            setEditingTemplate(null);
        } catch (error) {
            toast.error('Failed to save task');
        }
    };

    const addSubTask = () => {
        setEditingTemplate(prev => ({
            ...prev,
            subTasks: [...(prev?.subTasks || []), { label: '', order: (prev?.subTasks?.length || 0) + 1 }]
        }));
    };

    const reorderSubTasks = (newSubTasks: TaskSubTaskTemplate[]) => {
        setEditingTemplate(prev => {
            if (!prev) return prev;
            // Update order numbers based on new array position
            const updated = newSubTasks.map((st, i) => ({ ...st, order: i + 1 }));
            return { ...prev, subTasks: updated };
        });
    };

    const reorderSubSteps = (taskIndex: number, newSubSteps: TaskSubStepTemplate[]) => {
        const updated = newSubSteps.map((ss, i) => ({ ...ss, order: i + 1 }));
        updateSubTaskField(taskIndex, 'subSteps', updated);
    };

    const removeSubTask = (index: number) => {
        setEditingTemplate(prev => {
            const filtered = (prev?.subTasks || []).filter((_, i) => i !== index);
            const reassigned = filtered.map((st, i) => ({ ...st, order: i + 1 }));
            return { ...prev, subTasks: reassigned };
        });
    };

    const updateSubTaskField = (index: number, field: keyof TaskSubTaskTemplate, value: any) => {
        setEditingTemplate(prev => {
            const newSubTasks = [...(prev?.subTasks || [])];
            newSubTasks[index] = { ...newSubTasks[index], [field]: value };
            return { ...prev, subTasks: newSubTasks };
        });
    };

    const addSubStepToTask = (taskIndex: number) => {
        const currentSubSteps = editingTemplate?.subTasks?.[taskIndex]?.subSteps || [];
        updateSubTaskField(taskIndex, 'subSteps', [
            ...currentSubSteps, 
            { name: '', order: currentSubSteps.length + 1, required: true }
        ]);
    };

    const updateSubStepFieldInTask = (taskIndex: number, subStepIndex: number, field: keyof TaskSubStepTemplate, value: any) => {
        const currentSubSteps = [...(editingTemplate?.subTasks?.[taskIndex]?.subSteps || [])];
        currentSubSteps[subStepIndex] = { ...currentSubSteps[subStepIndex], [field]: value };
        updateSubTaskField(taskIndex, 'subSteps', currentSubSteps);
    };

    const removeSubStep = (taskIndex: number, subStepIndex: number) => {
        const currentSubSteps = (editingTemplate?.subTasks?.[taskIndex]?.subSteps || [])
            .filter((_, i) => i !== subStepIndex)
            .map((ss, i) => ({ ...ss, order: i + 1 }));
        updateSubTaskField(taskIndex, 'subSteps', currentSubSteps);
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Sub-Tabs Nav */}
            <div className="flex gap-4 px-4">
                <button 
                    onClick={() => setActiveSubTab('config')}
                    className={`px-6 py-2 rounded-2xl text-[11px] font-normal uppercase tracking-widest transition-all ${
                        activeSubTab === 'config' 
                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 dark:shadow-none' 
                            : 'bg-white dark:bg-slate-800 text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                >
                    Configuration
                </button>
                {isAdmin && (
                    <button 
                        onClick={() => setActiveSubTab('import')}
                        className={`px-6 py-2 rounded-2xl text-[11px] font-normal uppercase tracking-widest transition-all ${
                            activeSubTab === 'import' 
                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 dark:shadow-none' 
                                : 'bg-white dark:bg-slate-800 text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        Bulk Import
                    </button>
                )}
            </div>

            {activeSubTab === 'import' ? (
                <TaskImportView />
            ) : (
                <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-280px)]">
                    {/* Categories Sidebar */}
                    <div className="w-full md:w-64 flex flex-col bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
                    <h3 className="text-xs font-normal text-gray-400 uppercase tracking-widest">Categories</h3>
                    <button 
                        onClick={() => {
                            setEditingCategory({ name: '', color: '#6366f1' });
                            setIsEditingCategory(true);
                        }}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                    >
                        <Icons.Plus className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {categoriesList.map(cat => (
                        <div key={cat.id} className="group relative">
                            <button
                                onClick={() => setSelectedCategoryId(cat.id)}
                                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-normal transition-all flex items-center gap-3 ${
                                    selectedCategoryId === cat.id
                                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 dark:shadow-none'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                                }`}
                            >
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                <span className="truncate">{cat.name}</span>
                            </button>
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 flex gap-1 bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm border border-gray-100 dark:border-slate-700 transition-opacity">
                                <button 
                                    onClick={() => {
                                        setEditingCategory(cat);
                                        setIsEditingCategory(true);
                                    }}
                                    className="p-1 text-gray-400 hover:text-indigo-600"
                                >
                                    <Icons.Edit className="w-3.5 h-3.5" />
                                </button>
                                <button 
                                    onClick={() => {
                                        if (confirm('Delete this category? it will not delete templates but they will be unassigned.')) {
                                            database.deleteTaskCategory(cat.id);
                                        }
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                >
                                    <Icons.Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                    {categoriesList.length === 0 && (
                        <div className="p-8 text-center">
                            <Icons.FolderPlus className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-[10px] font-normal text-gray-400 uppercase tracking-widest leading-relaxed">No Categories<br/>Created Yet</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Task Templates List */}
            <div className="flex-1 flex flex-col bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
                    <div className="flex items-center gap-2">
                        <Icons.List className="w-4 h-4 text-indigo-600" />
                        <h3 className="text-xs font-normal text-gray-400 uppercase tracking-widest">
                            {selectedCategoryId ? `${categories[selectedCategoryId]?.name} Tasks` : 'Select a Category'}
                        </h3>
                    </div>
                    {selectedCategoryId && (
                        <button 
                            onClick={() => {
                                setEditingTemplate({ title: '', subTasks: [], active: true });
                                setIsEditingTemplate(true);
                            }}
                            className="px-4 py-2 bg-indigo-600 text-white text-[10px] font-normal rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none uppercase tracking-widest flex items-center gap-2"
                        >
                            <Icons.Plus className="w-3.5 h-3.5" />
                            Add Task
                        </button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {!selectedCategoryId ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <div className="w-16 h-16 bg-gray-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-4">
                                <Icons.ArrowLeft className="w-8 h-8 animate-pulse" />
                            </div>
                            <p className="text-sm font-normal uppercase tracking-widest">Select a category to view tasks</p>
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                             <div className="w-16 h-16 bg-gray-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-4">
                                <Icons.ClipboardList className="w-8 h-8 opacity-20" />
                            </div>
                            <p className="text-sm font-normal uppercase tracking-widest">No tasks in this category</p>
                            <button 
                                onClick={() => {
                                    setEditingTemplate({ title: '', subTasks: [], active: true });
                                    setIsEditingTemplate(true);
                                }}
                                className="mt-4 text-xs font-normal text-indigo-600 hover:text-indigo-700 uppercase tracking-widest"
                            >
                                Create your first task
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            {filteredTemplates.map(tpl => (
                                <div key={tpl.id} className="group p-5 bg-gray-50 dark:bg-slate-900/50 rounded-2xl border border-gray-100 dark:border-slate-800 transition-all hover:border-indigo-100 dark:hover:border-indigo-900/30">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="text-sm font-normal text-gray-900 dark:text-white uppercase tracking-tight">{tpl.title}</h4>
                                            {tpl.description && <p className="text-[11px] text-gray-500 font-normal mt-1">{tpl.description}</p>}
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => {
                                                    setEditingTemplate(tpl);
                                                    setIsEditingTemplate(true);
                                                }}
                                                className="p-2 bg-white dark:bg-slate-800 text-gray-400 hover:text-indigo-600 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm transition-colors"
                                            >
                                                <Icons.Edit className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={async () => {
                                                    if (confirm('Are you sure you want to delete this task template?')) {
                                                        try {
                                                            await database.deleteTaskTemplate(tpl.id);
                                                            toast.success('Task deleted');
                                                        } catch (error) {
                                                            toast.error('Failed to delete task');
                                                        }
                                                    }
                                                }}
                                                className="p-2 bg-white dark:bg-slate-800 text-gray-400 hover:text-red-600 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm transition-colors"
                                            >
                                                <Icons.Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <div className="text-[9px] font-normal text-gray-400 uppercase tracking-black mb-2">Steps / Sub-Tasks ({tpl.subTasks?.length || 0})</div>
                                    {tpl.subTasks?.sort((a, b) => (a.order || 0) - (b.order || 0)).map((st, idx) => (
                                        <div key={idx} className="space-y-1">
                                            <div className="flex items-center gap-3 py-1.5 px-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-700/50">
                                                <div className={`w-1.5 h-1.5 rounded-full ${st.required ? 'bg-indigo-600' : 'bg-indigo-300'}`} title={st.required ? 'Required Step' : 'Optional Step'} />
                                                <span className="text-[11px] font-normal text-gray-700 dark:text-gray-300">
                                                    <span className="text-indigo-400/50 mr-1">{st.order || idx + 1}.</span> {st.label}
                                                    {st.required && <span className="ml-1 text-red-500 font-normal">*</span>}
                                                </span>
                                            </div>
                                            {(st.subSteps?.length || 0) > 0 && (
                                                <div className="ml-6 space-y-1">
                                                    {(st.subSteps || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((ss, ssIdx) => (
                                                        <div key={ssIdx} className="flex items-center gap-2 text-[10px] text-gray-500 font-normal italic">
                                                            <Icons.ChevronRight className="w-2 h-2 text-indigo-400" />
                                                            <span className="text-gray-400/50 mr-1">{(st.order || idx + 1)}.{ss.order || ssIdx + 1}</span> {ss.name}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )}

            {/* Category Edit Modal */}
            {isEditingCategory && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
                    <div className="bg-white dark:bg-slate-800 rounded-[32px] w-full max-w-md p-8 shadow-2xl border border-white/20">
                        <h2 className="text-xl font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-6">
                            {editingCategory?.id ? 'Edit Category' : 'New Category'}
                        </h2>
                        <form onSubmit={handleSaveCategory} className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2 ml-4">Category Name</label>
                                <input 
                                    type="text"
                                    value={editingCategory?.name || ''}
                                    onChange={e => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 text-sm font-normal text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                    placeholder="e.g. Housekeeping"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2 ml-4">Display Color</label>
                                <div className="flex gap-3 items-center">
                                    <input 
                                        type="color"
                                        value={editingCategory?.color || '#6366f1'}
                                        onChange={e => setEditingCategory(prev => ({ ...prev, color: e.target.value }))}
                                        className="w-12 h-12 rounded-xl cursor-pointer bg-transparent border-none"
                                    />
                                    <div className="flex-1 p-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700 font-mono text-xs font-normal text-gray-500 uppercase">
                                        {editingCategory?.color}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsEditingCategory(false)}
                                    className="flex-1 py-4 bg-gray-50 dark:bg-slate-900 text-gray-500 font-normal rounded-2xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-all uppercase tracking-widest"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-4 bg-indigo-600 text-white font-normal rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none uppercase tracking-widest"
                                >
                                    Save Category
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Template Edit Modal */}
            {isEditingTemplate && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
                    <div className="bg-white dark:bg-slate-800 rounded-[32px] w-full max-w-2xl p-8 shadow-2xl border border-white/20 overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-normal text-gray-900 dark:text-white uppercase tracking-tight">
                                {editingTemplate?.id ? 'Edit Task' : 'New Task'}
                            </h2>
                            <div className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-[10px] font-normal uppercase tracking-widest">
                                {categories[selectedCategoryId!]?.name}
                            </div>
                        </div>

                        <form onSubmit={handleSaveTemplate} className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2 ml-4">Task Title</label>
                                <input 
                                    type="text"
                                    value={editingTemplate?.title || ''}
                                    onChange={e => setEditingTemplate(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 text-sm font-normal text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                    placeholder="e.g. Daily Lobby Maintenance"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2 ml-4">Short Description (Optional)</label>
                                <textarea 
                                    value={editingTemplate?.description || ''}
                                    onChange={e => setEditingTemplate(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 text-sm font-normal text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none min-h-[80px]"
                                    placeholder="Describe the overall scope of this task..."
                                />
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center px-4">
                                    <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest">Task Steps</label>
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            addSubTask();
                                            setExpandedStepIndex((editingTemplate?.subTasks?.length || 0));
                                        }}
                                        className="text-[10px] font-normal text-indigo-600 hover:text-indigo-700 uppercase tracking-widest flex items-center gap-1"
                                    >
                                        <Icons.Plus className="w-3 h-3" />
                                        Add Step
                                    </button>
                                </div>
                                 <div className="space-y-3">
                                    <Reorder.Group axis="y" values={editingTemplate?.subTasks || []} onReorder={reorderSubTasks} className="space-y-3">
                                    {editingTemplate?.subTasks?.map((st, idx) => {
                                        const isExpanded = expandedStepIndex === idx;
                                        return (
                                            <Reorder.Item 
                                                key={idx} 
                                                value={st}
                                                className={`bg-gray-50/50 dark:bg-slate-900/30 rounded-2xl border transition-all duration-300 ${
                                                    isExpanded 
                                                        ? 'border-indigo-200 dark:border-indigo-900/50 ring-1 ring-indigo-50 dark:ring-0 p-5' 
                                                        : 'border-gray-100 dark:border-slate-700 p-3'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="cursor-grab active:cursor-grabbing p-1 text-gray-300 hover:text-indigo-400">
                                                        <Icons.GripVertical className="w-4 h-4" />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedStepIndex(isExpanded ? null : idx)}
                                                        className="p-1 text-gray-400 hover:text-indigo-600"
                                                    >
                                                        {isExpanded ? <Icons.ChevronDown className="w-4 h-4" /> : <Icons.ChevronRight className="w-4 h-4" />}
                                                    </button>
                                                    <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl px-4 py-2 flex items-center gap-3 border border-gray-100 dark:border-slate-700">
                                                        <div className="text-[10px] font-normal text-gray-400">
                                                            Step {st.order || idx + 1}
                                                        </div>
                                                        <input 
                                                            type="text"
                                                            value={st.label}
                                                            onChange={e => updateSubTaskField(idx, 'label', e.target.value)}
                                                            onFocus={() => setExpandedStepIndex(idx)}
                                                            className="flex-1 bg-transparent border-none text-xs font-normal text-gray-900 dark:text-white focus:ring-0 outline-none p-0"
                                                            placeholder="Step name (e.g., Verify Operations)"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => updateSubTaskField(idx, 'required', !st.required)}
                                                            className={`px-3 py-2 rounded-xl text-[9px] font-normal uppercase tracking-widest transition-all border ${
                                                                st.required 
                                                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' 
                                                                    : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-400 hover:text-indigo-600'
                                                            }`}
                                                        >
                                                            {st.required ? 'Req' : 'Opt'}
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={() => removeSubTask(idx)}
                                                            className="p-2 text-gray-300 hover:text-red-600 transition-colors"
                                                        >
                                                            <Icons.Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div className="mt-4 pl-11 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                        <div className="space-y-3">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-[9px] font-normal text-gray-400 uppercase tracking-widest">Instructional Guidance (Sub-steps)</span>
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => addSubStepToTask(idx)}
                                                                    className="text-xs text-indigo-500 hover:text-indigo-600 flex items-center gap-1 font-normal"
                                                                >
                                                                    <Icons.Plus className="w-3.5 h-3.5" />
                                                                    <span className="text-[9px] uppercase tracking-widest">Add Sub-step</span>
                                                                </button>
                                                            </div>
                                                             <Reorder.Group axis="y" values={st.subSteps || []} onReorder={(val) => reorderSubSteps(idx, val)} className="space-y-2">
                                                                {(st.subSteps || []).map((ss, ssIdx) => (
                                                                    <Reorder.Item key={ssIdx} value={ss} className="flex gap-2">
                                                                        <div className="cursor-grab active:cursor-grabbing p-1.5 text-gray-300 hover:text-indigo-400 flex items-center">
                                                                            <Icons.GripVertical className="w-3 h-3" />
                                                                        </div>
                                                                        <div className="flex-1 bg-white dark:bg-slate-800 rounded-lg px-3 py-1.5 flex items-center gap-2 border border-gray-100 dark:border-slate-700">
                                                                            <span className="text-[10px] font-normal text-indigo-400/50 w-8">
                                                                                {(st.order || idx + 1)}.{ss.order || ssIdx + 1}
                                                                            </span>
                                                                            <input 
                                                                                type="text"
                                                                                value={ss.name}
                                                                                onChange={e => updateSubStepFieldInTask(idx, ssIdx, 'name', e.target.value)}
                                                                                className="flex-1 bg-transparent border-none text-[10px] font-normal text-gray-600 dark:text-gray-400 focus:ring-0 outline-none p-0"
                                                                                placeholder={ss.useDoubleFields ? "Label for fields (e.g. No Shows)" : "Sub-step name..."}
                                                                                required
                                                                            />
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateSubStepFieldInTask(idx, ssIdx, 'useDoubleFields', !ss.useDoubleFields)}
                                                                            className={`px-2 py-1 rounded-lg text-[8px] font-normal uppercase tracking-tight transition-all border flex items-center gap-1 ${
                                                                                ss.useDoubleFields 
                                                                                    ? 'bg-indigo-50 border-indigo-200 text-indigo-600' 
                                                                                    : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 text-gray-400 hover:text-indigo-600'
                                                                            }`}
                                                                            title="Enable Count and Details fields for this sub-step"
                                                                        >
                                                                            <Icons.Columns className="w-2.5 h-2.5" />
                                                                            {ss.useDoubleFields ? 'Double' : 'Single'}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateSubStepFieldInTask(idx, ssIdx, 'required', !ss.required)}
                                                                            className={`px-2 py-1 rounded-lg text-[8px] font-normal uppercase tracking-tight transition-all border ${
                                                                                ss.required 
                                                                                    ? 'bg-rose-50 border-rose-200 text-rose-600' 
                                                                                    : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 text-gray-400 hover:text-indigo-600'
                                                                            }`}
                                                                        >
                                                                            {ss.required ? 'Req' : 'Opt'}
                                                                        </button>
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => removeSubStep(idx, ssIdx)}
                                                                            className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                                                                        >
                                                                            <Icons.X className="w-3 h-3" />
                                                                        </button>
                                                                    </Reorder.Item>
                                                                ))}
                                                                {(st.subSteps?.length || 0) === 0 && (
                                                                    <div className="py-3 px-4 bg-white/30 dark:bg-slate-800/20 rounded-xl border border-dashed border-gray-200 dark:border-slate-700/50 flex items-center gap-3">
                                                                        <Icons.Info className="w-3.5 h-3.5 text-gray-300" />
                                                                        <p className="text-[9px] text-gray-400 font-normal uppercase tracking-tight">No actionable sub-steps defined. Add some for mandatory checklists.</p>
                                                                    </div>
                                                                )}
                                                            </Reorder.Group>
                                                        </div>
                                                    </div>
                                                )}
                                            </Reorder.Item>
                                        );
                                    })}
                                    </Reorder.Group>
                                    {(!editingTemplate?.subTasks || editingTemplate.subTasks.length === 0) && (
                                        <div className="text-center p-8 border-2 border-dashed border-gray-100 dark:border-slate-700 rounded-2xl">
                                            <p className="text-[10px] font-normal uppercase tracking-widest">No steps defined. Tasks without steps can only be checked off as a whole.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsEditingTemplate(false)}
                                    className="flex-1 py-4 bg-gray-50 dark:bg-slate-900 text-gray-500 font-normal rounded-2xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-all uppercase tracking-widest"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-4 bg-indigo-600 text-white font-normal rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none uppercase tracking-widest"
                                >
                                    Save Task
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskConfigurationView;
